import { z } from 'zod/v4'
import { addressSchema, type Address } from '../addresses/types.ts'
import { assetMediaSchema, type AssetDataType, type AssetMedia } from '../assets/assetDataTypes.ts'
import { assetRelationTypeSchema, type AssetRelationType } from '../assets/assetRelationTypes.ts'
import type { AssetPrice } from '../assets/assetTypes.ts'
import type { InterestAssetMatchScore, PropertyInterestData } from '../interests/types.ts'
import type { QuestionnaireAnswers } from '../questionnaires/types.ts'

export type FeedItemType = 'asset' | 'lead' | 'feedback' | 'interest' | 'contact_request' | 'price'

export type FeedItemContext = 'add' | 'update' | 'response' | 'match'

export const feedItemTypeSchema = z.enum([
  'asset',
  'lead',
  'feedback',
  'interest',
  'contact_request',
  'price',
])

export const feedItemContextSchema = z.enum(['add', 'update', 'response', 'match'])

export type RawFeedItem = {
  id: string
  userId: string
  eventId: string
  itemType: FeedItemType
  itemContext: FeedItemContext
  itemId: string
  createdAt: string
  feedAddedAt: string
  feedSeenAt: string | null
  isNew: boolean
}

export type FeedEventGroup = {
  eventId: string
  items: RawFeedItem[]
  feedAddedAt: string
  isNew: boolean
}

export type EnrichedAssetOrganisation = {
  id: string
  name: string
  logoMedia: AssetMedia | null
  realtorName: string | null
}

export type EnrichedAsset = {
  id: string
  name: string
  address: Address | null
  media: AssetMedia[]
  prices: AssetPrice[]
  data: AssetDataType | null
  realtorCount: number
  isOwn: boolean
  relationType?: AssetRelationType
  organisation?: EnrichedAssetOrganisation | null
}

export type EnrichedLeadOrganisation = {
  id: string
  name: string
  logoMedia: AssetMedia | null
  realtorName: string | null
}

export type EnrichedLead = {
  id: string
  name: string
  phone: string | null
  email: string | null
  is_own: boolean
  is_purchased: boolean
  is_verified: boolean
  contactIds?: string[]
  organisation?: EnrichedLeadOrganisation | null
}

export type EnrichedInterest = {
  id: string
  contactId: string
  assetId?: string | null
  data: PropertyInterestData
}

export type EnrichedFeedback = {
  id: string
  contactId: string
  answers: QuestionnaireAnswers
  completedAt: string
}

export type EnrichedEvent = {
  id: string
  title: string
  eventType: 'showing' | 'appraisal' | 'meeting'
  eventDate: string | null
  assetId: string | null
}

export type EnrichedPrice = {
  id: string
  assetId: string
  price: number
  priceType: string
  createdAt: string
}

export type EnrichedMatchScore = {
  assetId: string
  interestId: string
  matchScore: number
  priceScore: number | null
  locationScore: number | null
  sizeScore: number | null
  buildingTypeScore: number | null
  otherScore: number | null
}

export type FeedEnrichedData = {
  assets: Record<string, EnrichedAsset>
  leads: Record<string, EnrichedLead>
  interests: Record<string, EnrichedInterest>
  feedbacks: Record<string, EnrichedFeedback>
  prices: Record<string, EnrichedPrice>
  matchScores: EnrichedMatchScore[]
}

export type EnrichedFeedEventGroup = FeedEventGroup & {
  enrichedData: Partial<FeedEnrichedData>
  primaryAsset: EnrichedAsset | null
  primaryLead: EnrichedLead | null
}

export type FeedResponse = {
  events: EnrichedFeedEventGroup[]
  enrichedData: FeedEnrichedData
}

export type FeedCounts = {
  lead: number
  asset: number
  total: number
}

export type FeedViewMode = 'leads' | 'assets'

export type GroupedLeadFeedItem = {
  id: string
  leadId: string
  feedEventIds: string[]
  feedAddedAt: string
  isNew: boolean
  isInterestNew: boolean
  lead: EnrichedLead
  interest: EnrichedInterest | null
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  matchingAssets: {
    asset: EnrichedAsset
    matchScore: EnrichedMatchScore | null
    isNew: boolean
  }[]
  totalMatchingAssetsCount: number
  totalFeedbackResponsesCount: number
}

export type GroupedAssetFeedItem = {
  id: string
  assetId: string
  feedEventIds: string[]
  feedAddedAt: string
  isNew: boolean
  asset: EnrichedAsset
  leads: {
    lead: EnrichedLead
    interest: EnrichedInterest | null
    feedback: EnrichedFeedback | null
    matchScore: EnrichedMatchScore | null
    isNew: boolean
  }[]
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  totalMatchingLeadsCount: number
  totalFeedbackResponsesCount: number
}

export type LeadFeedResponse = GroupedLeadFeedItem[]

export type AssetFeedResponse = GroupedAssetFeedItem[]

export type FeedCursor = {
  feedAddedAt: string
  itemId: string
}

export type PaginatedLeadFeedResponse = {
  items: GroupedLeadFeedItem[]
  nextCursor: FeedCursor | null
  hasMore: boolean
  totalCount: number
}

export type PaginatedAssetFeedResponse = {
  items: GroupedAssetFeedItem[]
  nextCursor: FeedCursor | null
  hasMore: boolean
  totalCount: number
}

export const enrichedAssetOrganisationSchema = z.object({
  id: z.string(),
  name: z.string(),
  logoMedia: assetMediaSchema.nullable(),
  realtorName: z.string().nullable(),
})

export const enrichedAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: addressSchema.nullable(),
  media: z.array(assetMediaSchema),
  prices: z.array(z.unknown()),
  data: z.unknown().nullable(),
  realtorCount: z.number(),
  isOwn: z.boolean(),
  relationType: assetRelationTypeSchema.optional(),
  organisation: enrichedAssetOrganisationSchema.nullable().optional(),
})

export const enrichedLeadSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  isOwn: z.boolean(),
})

export const enrichedInterestSchema = z.object({
  id: z.string(),
  contactId: z.string(),
  assetId: z.string().nullable().optional(),
  data: z.unknown(),
})

export const enrichedFeedbackSchema = z.object({
  id: z.string(),
  contactId: z.string(),
  answers: z.unknown(),
  completedAt: z.string(),
})

export const enrichedEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  eventType: z.enum(['showing', 'appraisal', 'meeting']),
  eventDate: z.string().nullable(),
  assetId: z.string().nullable(),
})

export type AssetFeedItemData = {
  id: string
  name: string
  address: Address | null
  media: AssetMedia[]
  prices: AssetPrice[]
  data: AssetDataType | null
  realtorCount: number
  isOwn: boolean
  relationType?: AssetRelationType
  organisation?: EnrichedAssetOrganisation | null
}

export type ContactFeedItemData = {
  id: string
  name: string
  phone: string | null
  email: string | null
}

export type EventFeedItemData = {
  id: string
  title: string
  eventType: 'showing' | 'appraisal' | 'meeting'
  eventDate: string | null
  assetId: string | null
  assetName: string | null
}

export type FeedbackResponseAsset = {
  id: string
  name: string
  address: Address | null
  media: AssetMedia[]
}

export type FeedbackResponseFeedItemData = {
  feedbackId: string
  contactId: string
  contactName: string
  asset: FeedbackResponseAsset
  answers: QuestionnaireAnswers
  completedAt: string
}

export type LeadAssetMatchLead = {
  id: string
  name: string
  interest: {
    id: string
    data: PropertyInterestData
  }
  media: AssetMedia[]
}

export type LeadAssetMatchAsset = {
  assetId: string
  assetName: string
  address: Address | null
  media: AssetMedia[]
  matchScores: InterestAssetMatchScore
}

export type LeadAssetMatchFeedItemData = {
  lead: LeadAssetMatchLead
  matchingAssets: LeadAssetMatchAsset[]
  isOwnLead: boolean
}

export type FeedItemData =
  | AssetFeedItemData
  | ContactFeedItemData
  | EventFeedItemData
  | FeedbackResponseFeedItemData
  | LeadAssetMatchFeedItemData

export type BaseFeedItem<T extends string, D extends FeedItemData> = {
  id: string
  userId: string
  itemType: T
  itemId: string
  itemData: D
  createdAt: string
  feedAddedAt: string
  feedSeenAt: string | null
  isNew: boolean
}

export type AssetFeedItem = BaseFeedItem<'asset', AssetFeedItemData>
export type ContactFeedItem = BaseFeedItem<'contact', ContactFeedItemData>
export type EventFeedItem = BaseFeedItem<'event', EventFeedItemData>
export type FeedbackResponseFeedItem = BaseFeedItem<
  'feedback_response',
  FeedbackResponseFeedItemData
>
export type LeadAssetMatchFeedItem = BaseFeedItem<'lead_asset_match', LeadAssetMatchFeedItemData>

export type ParsedFeedItem =
  | AssetFeedItem
  | ContactFeedItem
  | EventFeedItem
  | FeedbackResponseFeedItem
  | LeadAssetMatchFeedItem

export const assetFeedItemDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: addressSchema.nullable(),
  media: z.array(assetMediaSchema),
  prices: z.array(z.unknown()),
  data: z.unknown().nullable(),
  realtorCount: z.number(),
  isOwn: z.boolean(),
  relationType: assetRelationTypeSchema.optional(),
  organisation: enrichedAssetOrganisationSchema.nullable().optional(),
})

export const contactFeedItemDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
})

export const eventFeedItemDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  eventType: z.enum(['showing', 'appraisal', 'meeting']),
  eventDate: z.string().nullable(),
  assetId: z.string().nullable(),
  assetName: z.string().nullable(),
})

export const feedbackResponseFeedItemDataSchema = z.object({
  feedbackId: z.string(),
  contactId: z.string(),
  contactName: z.string(),
  asset: z.object({
    id: z.string(),
    name: z.string(),
    address: addressSchema.nullable(),
    media: z.array(assetMediaSchema),
  }),
  answers: z.unknown(),
  completedAt: z.string(),
})

export type HomeFeedCardKind = 'lead_match' | 'lead_feedback' | 'asset_match' | 'asset_added'

export type HomeFeedCardBase = {
  id: string
  kind: HomeFeedCardKind
  feedEventIds: string[]
  feedAddedAt: string
  isNew: boolean
}

export type HomeFeedCardLeadMatch = HomeFeedCardBase & {
  kind: 'lead_match'
  lead: EnrichedLead
  interest: EnrichedInterest | null
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  matchingAssets: { asset: EnrichedAsset; matchScore: EnrichedMatchScore | null; isNew: boolean }[]
  totalMatchingAssetsCount: number
  totalFeedbackResponsesCount: number
}

export type HomeFeedCardLeadFeedback = HomeFeedCardBase & {
  kind: 'lead_feedback'
  lead: EnrichedLead
  interest: EnrichedInterest | null
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  matchingAssets: { asset: EnrichedAsset; matchScore: EnrichedMatchScore | null; isNew: boolean }[]
  totalMatchingAssetsCount: number
  totalFeedbackResponsesCount: number
}

export type HomeFeedCardAssetMatch = HomeFeedCardBase & {
  kind: 'asset_match'
  asset: EnrichedAsset
  leads: {
    lead: EnrichedLead
    interest: EnrichedInterest | null
    feedback: EnrichedFeedback | null
    matchScore: EnrichedMatchScore | null
    isNew: boolean
  }[]
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  totalMatchingLeadsCount: number
  totalFeedbackResponsesCount: number
}

export type HomeFeedCardAssetAdded = HomeFeedCardBase & {
  kind: 'asset_added'
  asset: EnrichedAsset
  leads: {
    lead: EnrichedLead
    interest: EnrichedInterest | null
    feedback: EnrichedFeedback | null
    matchScore: EnrichedMatchScore | null
    isNew: boolean
  }[]
  feedbacks: (EnrichedFeedback & { isNew: boolean })[]
  totalMatchingLeadsCount: number
  totalFeedbackResponsesCount: number
}

export type HomeFeedCard =
  | HomeFeedCardLeadMatch
  | HomeFeedCardLeadFeedback
  | HomeFeedCardAssetMatch
  | HomeFeedCardAssetAdded

export type HomeFeedStackSummary = {
  totalCount: number
  leadCount: number
  assetCount: number
}

export type HomeFeedStackState = {
  summary: HomeFeedStackSummary
  card: HomeFeedCard | null
}

export const leadAssetMatchFeedItemDataSchema = z.object({
  lead: z.object({
    id: z.string(),
    name: z.string(),
    interest: z.object({
      id: z.string(),
      data: z.unknown(),
    }),
    media: z.array(assetMediaSchema),
  }),
  matchingAssets: z.array(
    z.object({
      assetId: z.string(),
      assetName: z.string(),
      address: addressSchema.nullable(),
      media: z.array(assetMediaSchema),
      matchScores: z.object({
        asset_id: z.string(),
        match_score: z.number().nullable(),
        price_score: z.number().nullable(),
        location_score: z.number().nullable(),
        size_score: z.number().nullable(),
        building_type_score: z.number().nullable(),
        other_score: z.number().nullable(),
      }),
    })
  ),
  isOwnLead: z.boolean(),
})
